import React, { useState, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Filter,
  Save,
  Trash2,
  X,
  Search,
  RotateCcw,
  Building2,
  MapPin,
  Star,
  FileCheck,
  ShoppingCart,
  Calendar,
  Bookmark,
} from 'lucide-react';
import type { UseLeadFiltersReturn } from '../../hooks/useLeadFilters';
import type { FilterCounts, LicenseStatus, MarketStatus } from '../../types/leads';
import { useActiveMarket } from '../../store/useMarketStore';

interface LeadFilterPanelProps {
  filters: UseLeadFiltersReturn['filters'];
  setFilters: UseLeadFiltersReturn['setFilters'];
  resetFilters: UseLeadFiltersReturn['resetFilters'];
  hasActiveFilters: boolean;
  savedSearches: UseLeadFiltersReturn['savedSearches'];
  savePreset: UseLeadFiltersReturn['savePreset'];
  loadPreset: UseLeadFiltersReturn['loadPreset'];
  deletePreset: UseLeadFiltersReturn['deletePreset'];
  filterCounts?: FilterCounts;
  isLoading?: boolean;
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  children,
  defaultOpen = true,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-slate-700 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gold/5 transition-colors"
      >
        <div className="flex items-center gap-2 text-ivory font-medium text-sm">
          {icon}
          {title}
        </div>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-stone-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-stone-500" />
        )}
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

interface TierCheckboxProps {
  tier: string;
  label: string;
  count?: number;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const TierCheckbox: React.FC<TierCheckboxProps> = ({
  tier,
  label,
  count,
  checked,
  onChange,
}) => {
  const tierColors: Record<string, string> = {
    Tier1: 'bg-emerald-500',
    Tier2: 'bg-amber-500',
    Tier3: 'bg-red-500',
  };

  return (
    <label className="flex items-center gap-3 py-1.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-gold focus:ring-gold focus:ring-offset-0 focus:ring-offset-slate-900"
      />
      <span
        className={`w-2 h-2 rounded-full ${tierColors[tier] || 'bg-gray-400'}`}
      />
      <span className="text-sm text-ivory group-hover:text-gold flex-1 transition-colors">
        {label}
      </span>
      {count !== undefined && (
        <span className="text-xs text-stone-500 bg-slate-800 px-2 py-0.5 rounded-full">
          {count}
        </span>
      )}
    </label>
  );
};

interface RangeInputProps {
  label: string;
  minValue: number | null;
  maxValue: number | null;
  onMinChange: (value: number | null) => void;
  onMaxChange: (value: number | null) => void;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  step?: number;
}

const RangeInput: React.FC<RangeInputProps> = ({
  label,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  minPlaceholder = 'Min',
  maxPlaceholder = 'Max',
  step = 1,
}) => {
  return (
    <div>
      <label className="block text-xs text-stone-500 mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={minValue ?? ''}
          onChange={(e) =>
            onMinChange(e.target.value ? Number(e.target.value) : null)
          }
          placeholder={minPlaceholder}
          step={step}
          className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold text-ivory placeholder:text-stone-400"
        />
        <span className="text-stone-500">-</span>
        <input
          type="number"
          value={maxValue ?? ''}
          onChange={(e) =>
            onMaxChange(e.target.value ? Number(e.target.value) : null)
          }
          placeholder={maxPlaceholder}
          step={step}
          className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold text-ivory placeholder:text-stone-400"
        />
      </div>
    </div>
  );
};

interface RadioGroupProps {
  name: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value,
  options,
  onChange,
}) => {
  return (
    <div className="space-y-2">
      {options.map((option) => (
        <label
          key={option.value}
          className="flex items-center gap-3 py-1 cursor-pointer group"
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={(e) => onChange(e.target.value)}
            className="w-4 h-4 border-slate-600 bg-slate-800 text-gold focus:ring-gold focus:ring-offset-0 focus:ring-offset-slate-900"
          />
          <span className="text-sm text-ivory group-hover:text-gold transition-colors">
            {option.label}
          </span>
        </label>
      ))}
    </div>
  );
};

interface SaveSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}

const SaveSearchModal: React.FC<SaveSearchModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSave(name.trim());
      setName('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-ivory">
            Save Search
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-stone-500 hover:text-ivory transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter search name..."
            autoFocus
            className="w-full px-4 py-3 text-sm bg-slate-800 border border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold text-ivory placeholder:text-stone-400 mb-4"
          />
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-ivory bg-slate-800 border border-slate-700 rounded-md hover:bg-gold/10 hover:border-gold/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="flex-1 px-4 py-2 text-sm font-semibold text-slate-900 bg-gold rounded-md hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const LeadFilterPanel: React.FC<LeadFilterPanelProps> = ({
  filters,
  setFilters,
  resetFilters,
  hasActiveFilters,
  savedSearches,
  savePreset,
  loadPreset,
  deletePreset,
  filterCounts,
  isLoading: _isLoading,
}) => {
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const market = useActiveMarket();

  const handleTierChange = useCallback(
    (tier: string, checked: boolean) => {
      const newTiers = checked
        ? [...filters.tiers, tier]
        : filters.tiers.filter((t) => t !== tier);
      setFilters({ tiers: newTiers });
    },
    [filters.tiers, setFilters]
  );

  const handleZipCodesChange = useCallback(
    (value: string) => {
      const zipCodes = value
        .split(',')
        .map((z) => z.trim())
        .filter((z) => z.length > 0);
      setFilters({ zipCodes });
    },
    [setFilters]
  );

  const getTierCount = (tier: string): number | undefined => {
    return filterCounts?.tier_counts?.find((t) => t.value === tier)?.count;
  };

  return (
    <div className="w-72 bg-slate-900 border-r border-slate-700 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gold" />
          <h2 className="font-semibold text-ivory">Filters</h2>
        </div>
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="flex items-center gap-1 px-2 py-1 text-xs text-stone-500 hover:text-gold transition-colors"
            title="Clear all filters"
          >
            <RotateCcw className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>

      {/* Active market badge */}
      <div className="px-4 py-2 border-b border-slate-700 bg-slate-800/50">
        <div className="flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5 text-gold" />
          <span className="text-xs text-stone-400">Market:</span>
          <span className="text-xs font-semibold text-gold">
            {market.display_name}, {market.state}
          </span>
        </div>
      </div>

      {/* Filter Sections */}
      <div className="flex-1 overflow-y-auto">
        {/* Tier Filter */}
        <CollapsibleSection
          title="Neighborhood Tier"
          icon={<Star className="w-4 h-4 text-amber-500" />}
        >
          <div className="space-y-1">
            <TierCheckbox
              tier="Tier1"
              label="Tier 1 - Stable"
              count={getTierCount('Tier1')}
              checked={filters.tiers.includes('Tier1')}
              onChange={(checked) => handleTierChange('Tier1', checked)}
            />
            <TierCheckbox
              tier="Tier2"
              label="Tier 2 - Emerging"
              count={getTierCount('Tier2')}
              checked={filters.tiers.includes('Tier2')}
              onChange={(checked) => handleTierChange('Tier2', checked)}
            />
            <TierCheckbox
              tier="Tier3"
              label="Tier 3 - Value"
              count={getTierCount('Tier3')}
              checked={filters.tiers.includes('Tier3')}
              onChange={(checked) => handleTierChange('Tier3', checked)}
            />
          </div>
        </CollapsibleSection>

        {/* Unit Count */}
        <CollapsibleSection
          title="Unit Count"
          icon={<Building2 className="w-4 h-4 text-blue-500" />}
        >
          <RangeInput
            label="Number of units"
            minValue={filters.unitMin}
            maxValue={filters.unitMax}
            onMinChange={(val) => setFilters({ unitMin: val })}
            onMaxChange={(val) => setFilters({ unitMax: val })}
            minPlaceholder="Min"
            maxPlaceholder="Max"
          />
        </CollapsibleSection>

        {/* Lead Score */}
        <CollapsibleSection
          title="Lead Score"
          icon={<Search className="w-4 h-4 text-purple-500" />}
        >
          <RangeInput
            label="Score range (0-100)"
            minValue={filters.scoreMin}
            maxValue={filters.scoreMax}
            onMinChange={(val) => setFilters({ scoreMin: val })}
            onMaxChange={(val) => setFilters({ scoreMax: val })}
            minPlaceholder="0"
            maxPlaceholder="100"
          />
        </CollapsibleSection>

        {/* License Status — Philadelphia only (rental license is a Philly-specific dataset) */}
        {market.market_id === 'philadelphia' && (
          <CollapsibleSection
            title="License Status"
            icon={<FileCheck className="w-4 h-4 text-green-500" />}
          >
            <RadioGroup
              name="licenseStatus"
              value={filters.licenseStatus}
              options={[
                { value: 'any', label: 'Any' },
                { value: 'licensed', label: 'Licensed' },
                { value: 'unlicensed', label: 'Unlicensed' },
              ]}
              onChange={(val) =>
                setFilters({ licenseStatus: val as LicenseStatus })
              }
            />
          </CollapsibleSection>
        )}

        {/* Market Status */}
        <CollapsibleSection
          title="Market Status"
          icon={<ShoppingCart className="w-4 h-4 text-orange-500" />}
        >
          <RadioGroup
            name="marketStatus"
            value={filters.marketStatus}
            options={[
              { value: 'any', label: 'Any' },
              { value: 'on-market', label: 'On Market' },
              { value: 'off-market', label: 'Off Market' },
            ]}
            onChange={(val) =>
              setFilters({ marketStatus: val as MarketStatus })
            }
          />
        </CollapsibleSection>

        {/* ZIP Code */}
        <CollapsibleSection
          title="ZIP Code"
          icon={<MapPin className="w-4 h-4 text-red-500" />}
        >
          <div>
            <label className="block text-xs text-stone-500 mb-2">
              Enter ZIP codes (comma-separated)
            </label>
            <input
              type="text"
              value={filters.zipCodes.join(', ')}
              onChange={(e) => handleZipCodesChange(e.target.value)}
              placeholder={`e.g., ${market.zip_prefix}04, ${market.zip_prefix}43, ${market.zip_prefix}44`}
              className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold text-ivory placeholder:text-stone-400"
            />
          </div>
        </CollapsibleSection>

        {/* Date Range */}
        <CollapsibleSection
          title="Date Range"
          icon={<Calendar className="w-4 h-4 text-cyan-500" />}
          defaultOpen={false}
        >
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-stone-500 mb-2">From</label>
              <input
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) =>
                  setFilters({ dateFrom: e.target.value || null })
                }
                className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold text-ivory"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-2">To</label>
              <input
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) =>
                  setFilters({ dateTo: e.target.value || null })
                }
                className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold text-ivory"
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* Saved Searches */}
        <CollapsibleSection
          title="Saved Searches"
          icon={<Bookmark className="w-4 h-4 text-indigo-500" />}
          defaultOpen={false}
        >
          <div className="space-y-3">
            {/* Save Current Button */}
            {hasActiveFilters && (
              <button
                onClick={() => setIsSaveModalOpen(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gold bg-gold/10 border border-gold/30 rounded-md hover:bg-gold/20 transition-colors"
              >
                <Save className="w-4 h-4" />
                Save Current Search
              </button>
            )}

            {/* Saved Searches List */}
            {savedSearches.length > 0 ? (
              <div className="space-y-2">
                {savedSearches.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center gap-2 p-2 bg-slate-800 rounded-md group"
                  >
                    <button
                      onClick={() => loadPreset(preset.id)}
                      className="flex-1 text-left text-sm text-ivory hover:text-gold transition-colors truncate"
                      title={preset.name}
                    >
                      {preset.name}
                    </button>
                    <button
                      onClick={() => deletePreset(preset.id)}
                      className="p-1 text-stone-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                      title="Delete search"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-stone-500 text-center py-2">
                No saved searches yet
              </p>
            )}
          </div>
        </CollapsibleSection>
      </div>

      {/* Footer Actions */}
      <div className="px-4 py-3 border-t border-slate-700 space-y-2">
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-ivory bg-slate-800 border border-slate-700 rounded-md hover:bg-gold/10 hover:border-gold/50 transition-colors"
          >
            <X className="w-4 h-4" />
            Clear All Filters
          </button>
        )}
        {filterCounts?.totalMatching !== undefined && (
          <p className="text-xs text-stone-500 text-center">
            <span className="text-gold font-mono">{filterCounts.totalMatching.toLocaleString()}</span> matching leads
          </p>
        )}
      </div>

      {/* Save Search Modal */}
      <SaveSearchModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={savePreset}
      />
    </div>
  );
};

export default LeadFilterPanel;
